import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loading, user, profile, signOut } = useAuth();
  const location = useLocation();
  const [timeoutReached, setTimeoutReached] = useState(false);
  const prevPathRef = useRef<string>(location.pathname);
  const prevUserRef = useRef<string | undefined>(user?.id);
  
  // Only log when relevant changes occur
  const shouldLog = prevPathRef.current !== location.pathname || 
                    prevUserRef.current !== user?.id || 
                    loading;
                    
  if (shouldLog) {
    console.log('RequireAuth state:', { 
      loading, 
      userEmail: user?.email,
      profileEmail: profile?.email,
      currentPath: location.pathname,
      hasUser: !!user,
      hasProfile: !!profile
    });
    
    // Update refs to current values
    prevPathRef.current = location.pathname;
    prevUserRef.current = user?.id;
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.error('Auth loading timeout reached');
        setTimeoutReached(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  if (timeoutReached) {
    return <Navigate to="/login" state={{ from: location, error: 'timeout' }} replace />;
  }

  // Show spinner while loading OR while user is set but profile hasn't
  // arrived yet (async fetch in progress). Never redirect during this window.
  if (loading || (user && !profile)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A deactivated teammate keeps a valid session until it expires, so the app
  // has to turn them away itself. The database already refuses their writes;
  // this stops them sitting inside a workspace that will only error.
  if (profile && profile.is_active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">Your access has been turned off</h1>
          <p className="mt-2 text-sm text-gray-600">
            An administrator deactivated this account. Ask them to turn it back on if you think
            this is a mistake.
          </p>
          <button
            type="button"
            onClick={() => { void signOut(); }}
            className="mt-4 inline-flex h-10 items-center rounded-md bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
